import { useMemo, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePublicVendorServices } from '../services/vendorService';

// Helper function to transform API service data to component format
const transformServiceData = (services) => {
  if (!services || !Array.isArray(services)) return [];
  
  return services.map((service) => {
    // Handle both camelCase and snake_case API responses
    const vendorProfile = service.vendor_profile || service.vendorProfile;
    const vendorUser = vendorProfile?.user || vendorProfile?.User;
    
    const city = vendorProfile?.city || '';
    const state = vendorProfile?.state || '';
    const locationStr = city && state 
      ? `${city}, ${state}` 
      : city || state || 'Location not available';
    
    return {
      id: service.id,
      title: service.service_name || service.serviceName || 'Service',
      rating: service.average_rating || service.averageRating || vendorProfile?.average_rating || vendorProfile?.averageRating || 0,
      price: parseFloat(service.base_price || service.basePrice || 0),
      image: service.image_url || 
             service.imageUrl ||
             vendorProfile?.image_url || 
             vendorProfile?.imageUrl ||
             vendorUser?.profile_image_url ||
             vendorUser?.profileImageUrl ||
             'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=400&h=300&fit=crop',
      location: locationStr,
      isFeatured: service.is_featured || service.isFeatured || false,
      // Include vendor information for CategoryServices page
      vendor_profile: vendorProfile,
      vendor: vendorProfile?.business_name || vendorProfile?.businessName || 'Vendor',
      reviewCount: service.total_reviews || service.totalReviews || vendorProfile?.total_reviews || vendorProfile?.totalReviews || 0
    };
  });
};

/**
 * Hook to fetch and transform vendor services for a specific category
 * @param {string} categoryId - The category ID to fetch services for
 * @param {object} options - Additional options (per_page, sort_by, sort_order, location)
 * @returns {object} - { services, isLoading, error }
 */
export const useCategoryServices = (categoryId, options = {}) => {
  const queryClient = useQueryClient();
  
  // Extract location values for stable dependency tracking
  const location = options.location || {};
  const locationLat = location.latitude;
  const locationLng = location.longitude;
  const locationCountry = location.country;
  const locationCity = location.city;
  const locationState = location.state;

  // Use a ref to store previous services data
  const previousServicesRef = useRef([]);
  // Store previous location to detect significant changes
  const previousLocationRef = useRef(null);

  // Memoize params to prevent unnecessary refetches
  const params = useMemo(() => {
    const baseParams = {
      service_category_id: categoryId,
      per_page: options.per_page || 12,
      sort_by: options.sort_by || 'created_at',
      sort_order: options.sort_order || 'desc'
    };

    // Add location-based filtering and sorting if available
    // Only add location params if location is stable (not changing rapidly)
    // This prevents over-filtering that might return empty results
    if (locationLat && locationLng) {
      baseParams.latitude = locationLat;
      baseParams.longitude = locationLng;
      // Only filter by country if explicitly provided (don't auto-filter)
      // This allows showing services even if country detection is wrong
      if (locationCountry && options.filterByCountry !== false) {
        baseParams.country = locationCountry;
      }
      // Use distance sorting when location is provided (unless explicitly overridden)
      if (!options.sort_by || options.sort_by === 'created_at') {
        baseParams.sort_by = 'distance';
        baseParams.sort_order = 'asc';
      }
    } else if (locationCity && options.filterByCity !== false) {
      baseParams.city = locationCity;
    } else if (locationState && options.filterByState !== false) {
      baseParams.state = locationState;
    }

    return baseParams;
  }, [
    categoryId,
    options.per_page,
    options.sort_by,
    options.sort_order,
    options.filterByCountry,
    options.filterByCity,
    options.filterByState,
    locationLat,
    locationLng,
    locationCountry,
    locationCity,
    locationState
  ]);

  // Clear previous data when location changes significantly
  useEffect(() => {
    const currentLocation = {
      lat: locationLat,
      lng: locationLng,
      country: locationCountry,
      city: locationCity,
      state: locationState
    };
    
    const previousLocation = previousLocationRef.current;
    
    // If location changed significantly (different country or coordinates changed by more than 0.1 degrees)
    if (previousLocation) {
      const countryChanged = previousLocation.country !== currentLocation.country;
      const coordsChanged = previousLocation.lat && previousLocation.lng && currentLocation.lat && currentLocation.lng &&
        (Math.abs(previousLocation.lat - currentLocation.lat) > 0.1 || 
         Math.abs(previousLocation.lng - currentLocation.lng) > 0.1);
      
      if (countryChanged || coordsChanged) {
        // Clear previous data when location changes significantly
        previousServicesRef.current = [];
        // Invalidate queries to force fresh fetch
        queryClient.invalidateQueries({ 
          queryKey: ['public-vendor-services', params] 
        });
        if (process.env.NODE_ENV === 'development') {
          console.log('useCategoryServices - Location changed significantly, clearing cache', {
            previous: previousLocation,
            current: currentLocation
          });
        }
      }
    }
    
    // Update previous location
    previousLocationRef.current = currentLocation;
  }, [locationLat, locationLng, locationCountry, locationCity, locationState, params, queryClient]);
  
  const { data: servicesResponse, isLoading, error, isFetching } = usePublicVendorServices(params, {
    placeholderData: undefined, // Don't keep previous data - show loading when location changes
    staleTime: 0, // Always consider data stale to ensure fresh fetch on location change
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: true, // Always refetch on mount
  });

  const services = useMemo(() => {
    // Debug logging
    if (process.env.NODE_ENV === 'development') {
      // console.log('useCategoryServices - servicesResponse:', servicesResponse);
      // console.log('useCategoryServices - isLoading:', isLoading);
      // console.log('useCategoryServices - isFetching:', isFetching);
      // console.log('useCategoryServices - error:', error);
      // console.log('useCategoryServices - params:', params);
    }
    
    // If we're loading and have no response yet, return empty array (don't show stale data)
    if (isLoading && !servicesResponse && !error) {
      return [];
    }
    
    // If there's an error and no data, return empty array
    if (error && !servicesResponse) {
      console.error('useCategoryServices - Error fetching services:', error);
      return [];
    }
    
    // If no response, return empty array
    if (!servicesResponse) {
      return [];
    }
    
    // Only return empty array if there's a definitive error response
    if (servicesResponse?.success === false) {
      console.error('useCategoryServices - API returned success: false', servicesResponse);
      return [];
    }
    
    // Handle different response structures
    let servicesData = [];
    
    // Laravel paginated response structure: { success: true, data: { data: [...], current_page: ... } }
    if (servicesResponse?.data?.data && Array.isArray(servicesResponse.data.data)) {
      servicesData = servicesResponse.data.data;
      if (process.env.NODE_ENV === 'development') {
        console.log('useCategoryServices - Found paginated data:', servicesData.length, 'items');
      }
    }
    // Direct array or non-paginated response
    else if (Array.isArray(servicesResponse?.data)) {
      servicesData = servicesResponse.data;
      if (process.env.NODE_ENV === 'development') {
        console.log('useCategoryServices - Found direct array data:', servicesData.length, 'items');
      }
    }
    // Fallback to response itself if it's an array
    else if (Array.isArray(servicesResponse)) {
      servicesData = servicesResponse;
      if (process.env.NODE_ENV === 'development') {
        console.log('useCategoryServices - Found root array data:', servicesData.length, 'items');
      }
    }
    // Check if data exists but in a different structure
    else if (servicesResponse?.data) {
      console.warn('useCategoryServices - Unexpected data structure:', servicesResponse);
    }
    
    const transformed = transformServiceData(servicesData);
    
    // Update previous data with new data
    previousServicesRef.current = transformed;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('useCategoryServices - Transformed services:', transformed.length, 'items');
    }
    
    // Return the transformed data
    const result = transformed;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('useCategoryServices - Transformed services:', transformed.length, 'items');
      console.log('useCategoryServices - Returning services:', result.length, 'items');
    }
    
    return result;
  }, [servicesResponse, isLoading, isFetching, error, params]);

  return {
    services,
    isLoading,
    error
  };
};

